/**
 * Roads (03 §5.1 step 4): the main road from the south edge through every road crossing to
 * the compound, a side road from the side gate to the map edge, and the reserved corridor
 * from the compound to the pickup. Roads are also where mines never go, which is what
 * guarantees a mine-free way north.
 */

import { FLAG } from './grid';
import { LINES } from './tuning';
import type { GenCtx } from './types';

export function buildRoads(ctx: GenCtx): void {
  const rng = ctx.rng.fork('roads');
  const { plan, map, spec } = ctx;
  const first = plan.lines[0];
  const points: Array<[number, number]> = [];
  if (first) points.push([first.roadX + rng.range(-5, 5), ctx.sizeZ]);
  let prev: [number, number] | null = null;
  for (const line of plan.lines) {
    if (prev) {
      // A bend in the middle of each band keeps the road from being a ruler line.
      points.push([(prev[0] + line.roadX) / 2 + rng.range(-3, 3), (prev[1] + line.z) / 2]);
    }
    // Square through the crossing so the road fits the gap.
    points.push([line.roadX, line.z + 4], [line.roadX, line.z - 4]);
    prev = [line.roadX, line.z - 4];
  }
  points.push([plan.roadEnd.x, plan.roadEnd.z + 5]);
  if (spec.layout === 'border') points.push([plan.roadEnd.x, 0]);
  else if (spec.perimeter === 'none' || spec.sealed) points.push([plan.roadEnd.x, plan.roadEnd.z + 1]);
  // Through the gate and a little way in: the rest of the compound is the archetype's to lay out.
  else points.push([plan.roadEnd.x, plan.roadEnd.z - 7]);
  map.road(points, LINES.roadWidth);

  const side = plan.gates.find((g) => g.side !== 'south');
  if (side) {
    const edgeX = side.side === 'west' ? 0 : ctx.sizeX;
    const bend: [number, number] = [(side.x + edgeX) / 2, side.z + rng.range(-3, 3)];
    map.road([[side.x, side.z], bend, [edgeX, side.z + rng.range(-6, 6)]], 3.4);
  }
}

/** Reserve the start, the pickup, the gates, and a walkable corridor from the compound to the pickup. */
export function reserveKeyGround(ctx: GenCtx): void {
  const { plan, map } = ctx;
  map.mask.circle(plan.player.x, plan.player.z, 5, FLAG.keep);
  map.mask.circle(plan.pickup.x, plan.pickup.z, 5, FLAG.keep);
  for (const g of plan.gates) map.mask.circle(g.x, g.z, g.width / 2 + 1.5, FLAG.keep);
  if (ctx.spec.layout !== 'compound') return;
  const c = plan.compound;
  // Round the south-side corner of the compound, a few metres off the perimeter.
  const cornerX = plan.roadEast ? c.x0 - 5 : c.x1 + 5;
  const corridor: Array<[number, number]> = [
    [plan.roadEnd.x, c.z1 + 4], [cornerX, c.z1 + 4], [cornerX, plan.pickup.z], [plan.pickup.x, plan.pickup.z],
  ];
  map.mask.line(corridor, 1.2, FLAG.path | FLAG.keep);
  const side = plan.gates.find((g) => g.side !== 'south');
  if (side) map.mask.line([[side.x, side.z], [plan.pickup.x, plan.pickup.z]], 1.2, FLAG.path | FLAG.keep);
}
